import React, { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { backendProvider } from "../../services/backend";
import { getValidationError, getUserFriendlyError } from "../../utils/errorMessages";
import { geoService } from "../../services/geo.service";
import { detectLocale } from "../../services/localeDetection.service";
import { GeoSelect } from "../atoms/GeoSelect";
import {
  PICKER_LOCALE_OPTIONS,
  DEFAULT_LOCALE,
} from "../../constants/locales";

interface NewUserRegistrationModalProps {
  isOpen: boolean;
  userEmail: string;
  userName?: string;
  onComplete: () => void;
}

type RegistrationStatus = "idle" | "submitting" | "success" | "error";

const normalizeWebsiteUrl = (value: string): string => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return "";
  return /^https?:\/\//i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`;
};

export const NewUserRegistrationModal: React.FC<
  NewUserRegistrationModalProps
> = ({ isOpen, userEmail, userName: initialUserName, onComplete }) => {
  const [userName, setUserName] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientWebsite, setClientWebsite] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  // ISO2 code (e.g. "ES") from the Country selector — drives the Team's
  // timezone/currency defaults saved in Appwrite Team preferences.
  // Spain is the default market (matches the Seller form default).
  const [countryCode, setCountryCode] = useState("ES");
  // State/province name (e.g. "Barcelona") — refines the timezone default.
  const [stateName, setStateName] = useState("Barcelona");
  // BCP 47 locale code (e.g. "en-GB") — stored directly by the GeoSelect picker.
  const [preferredLanguage, setPreferredLanguage] = useState<string>(DEFAULT_LOCALE);

  // --- Auto-detection (IP/browser) — pre-fills the geo/language pickers ONCE,
  //     before the user touches them. Never overrides a manual selection. ---
  const userTouchedGeo = useRef(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;

    detectLocale().then((detected) => {
      if (cancelled || userTouchedGeo.current) return;
      if (detected.language) setPreferredLanguage(detected.language);
      if (detected.countryCode) setCountryCode(detected.countryCode);
      if (detected.stateName) setStateName(detected.stateName);
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);
  const [status, setStatus] = useState<RegistrationStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialUserName && !userName) {
      setUserName(initialUserName);
      console.log("📝 Pre-filled user name from OAuth:", initialUserName);
    }
  }, [initialUserName, userName]);

  // Geo countries — same source as the Seller form dropdown (cached). Hooks
  // must run before the `!isOpen` early return to keep hook order stable.
  const { data: countries = [], isLoading: countriesLoading } = useQuery({
    queryKey: ['geo', 'countries'],
    queryFn: () => geoService.listCountries(),
    staleTime: Infinity,
    retry: 1,
  });
  // States cascade from the selected country (cached per country).
  const { data: states = [], isLoading: statesLoading } = useQuery({
    queryKey: ['geo', 'states', countryCode],
    queryFn: () => geoService.listStates(countryCode),
    enabled: Boolean(countryCode),
    staleTime: Infinity,
    retry: 1,
  });

  const countryOptions = useMemo(
    () => countries.map((c) => ({ value: c.iso2, label: c.name, emoji: c.emoji })),
    [countries],
  );
  const stateOptions = useMemo(
    () => states.map((s) => ({ value: s.name, label: s.name })),
    [states],
  );
  // Business-relevant languages first (LLM-translated), then all others.
  const localeOptions = useMemo(
    () => PICKER_LOCALE_OPTIONS.map((opt) => ({ value: opt.code, label: opt.label, emoji: opt.flag })),
    [],
  );

  // Keep the state selection valid when the country changes: normalize a
  // case-insensitive near-match (IP regions vary in casing), else reset.
  useEffect(() => {
    if (!states.length) return;
    if (states.some((s) => s.name === stateName)) return;
    const ciMatch = states.find(
      (s) => s.name.toLowerCase() === stateName.trim().toLowerCase(),
    );
    setStateName(ciMatch ? ciMatch.name : '');
  }, [states, stateName]);

  const handleCountryChange = (iso2: string) => {
    userTouchedGeo.current = true;
    setCountryCode(iso2);
    setStateName('');
  };

  if (!isOpen) return null;

  const appwriteEndpoint = import.meta.env.VITE_APPWRITE_ENDPOINT;
  const appwriteProjectId = import.meta.env.VITE_APPWRITE_PROJECT_ID;
  const registrationBackgroundBucketId =
    import.meta.env.VITE_APPWRITE_BUCKET_IMAGE_BACKGROUNDS || "image-backgrounds";
  const registrationBackgroundFileId =
    import.meta.env.VITE_REGISTRATION_MODAL_BG_FILE_ID || "1";
  const registrationBackgroundUrl =
    appwriteEndpoint && appwriteProjectId
      ? `${appwriteEndpoint}/storage/buckets/${registrationBackgroundBucketId}/files/${registrationBackgroundFileId}/view?project=${appwriteProjectId}`
      : null;

  // Borderless filled inputs — modern look: background fill only, no border
  // lines; a soft ring appears on focus (rose ring on validation error).
  const baseInputClass =
    "w-full rounded-2xl border border-transparent px-4 py-3 text-[15px] outline-none transition-all duration-200 placeholder:text-slate-500";

  const editableInputClass = (fieldError?: string) =>
    `${baseInputClass} bg-slate-950/70 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] ${
      fieldError
        ? "ring-1 ring-rose-500/40 focus:ring-2 focus:ring-rose-500/30"
        : "focus:bg-slate-900/80 focus:ring-2 focus:ring-cyan-300/20"
    }`;

  const readOnlyInputClass =
    `${baseInputClass} bg-cyan-400/10 pr-24 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]`;

  const validateField = (field: string, value: string): string | null => {
    return getValidationError(field, value);
  };

  const handleFieldChange = (field: string, value: string) => {
    // Clear field error when user starts typing
    setFieldErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });

    // Update field value
    switch (field) {
      case "fullName":
        setUserName(value);
        break;
      case "companyName":
        setClientName(value);
        break;
      case "companyWebsite":
        setClientWebsite(value);
        break;
      case "jobTitle":
        setJobTitle(value);
        break;
    }
  };

  const handleWebsiteBlur = () => {
    setClientWebsite((currentValue) => normalizeWebsiteUrl(currentValue));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    // Validate all fields
    const errors: Record<string, string> = {};

    const nameError = validateField("fullName", userName);
    if (nameError) errors.fullName = nameError;

    const companyError = validateField("companyName", clientName);
    if (companyError) errors.companyName = companyError;

    const websiteError = validateField("companyWebsite", clientWebsite);
    if (websiteError) errors.companyWebsite = websiteError;

    const jobError = validateField("jobTitle", jobTitle);
    if (jobError) errors.jobTitle = jobError;

    if (!countryCode) {
      errors.country = "Please select your country.";
    }

    // If validation errors exist, show them
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setStatus("error");
      return;
    }

    setStatus("submitting");

    try {
      console.log("📝 Completing user registration with Appwrite...");
      await backendProvider.auth.completeRegistration({
        fullName: userName.trim(),
        companyName: clientName.trim(),
        companyWebsite: normalizeWebsiteUrl(clientWebsite) || undefined,
        jobTitle: jobTitle.trim() || undefined,
        preferredLanguage,
        countryCode,
        stateName: stateName || undefined,
      });

      console.log("✅ Registration completed successfully!");
      setStatus("success");

      setTimeout(() => {
        onComplete();
      }, 2000);
    } catch (err: any) {
      console.error("❌ Registration error:", err);
      // Use user-friendly error messages
      const friendlyError = getUserFriendlyError(err);
      setError(friendlyError.message);
      setStatus("error");
    }
  };

  const renderContent = () => {
    switch (status) {
      case "submitting":
        return (
          <div className="flex min-h-[480px] flex-col items-center justify-center px-8 py-14 text-center text-white">
            {/* Gradient conic ring — brand cyan→indigo, replaces the flat spinner */}
            <div className="relative mb-7 h-14 w-14">
              <div
                className="absolute inset-0 animate-spin rounded-full"
                style={{
                  background:
                    "conic-gradient(from 0deg, transparent 0%, rgba(125,211,252,0.9) 35%, rgba(82,96,255,0.95) 70%, transparent 100%)",
                  maskImage:
                    "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px))",
                  WebkitMaskImage:
                    "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px))",
                }}
              />
              <div className="absolute inset-0 rounded-full bg-[conic-gradient(from_180deg,transparent_0%,rgba(82,96,255,0.25)_100%)] blur-md" />
            </div>

            <div className="mb-1.5 inline-flex items-center gap-2 rounded-full border border-cyan-400/15 bg-cyan-400/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-cyan-200">
              New Client Setup
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-white">
              Finalizing your setup
            </h2>
            <p className="mt-2 max-w-sm text-sm leading-6 text-slate-400">
              Please wait a moment — we are creating your workspace and
              preparing your Brainiacs account.
            </p>

            {/* Soft progress dots — conveys staged work without a progress bar */}
            <div className="mt-8 flex items-center gap-1.5">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="h-1.5 w-1.5 animate-pulse rounded-full bg-cyan-300/80"
                  style={{ animationDelay: `${i * 240}ms` }}
                />
              ))}
            </div>
          </div>
        );
      case "success":
        return (
          <div className="flex min-h-[480px] flex-col items-center justify-center px-8 py-14 text-center text-white">
            <div className="mb-7 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-400/10 ring-1 ring-emerald-300/30">
              <svg
                className="h-7 w-7 text-emerald-300"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            </div>

            <div className="mb-1.5 inline-flex items-center gap-2 rounded-full border border-emerald-400/15 bg-emerald-400/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-emerald-200">
              New Client Setup
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-white">
              Registration successful
            </h2>
            <p className="mt-2 max-w-sm text-sm leading-6 text-slate-400">
              Your workspace is ready. Redirecting you to the dashboard…
            </p>
          </div>
        );
      case "error":
      case "idle":
      default:
        return (
          <div className="grid min-h-[620px] gap-0 lg:grid-cols-[0.8fr_1.2fr]">
            <div
              className="relative hidden overflow-hidden rounded-[28px] bg-[radial-gradient(circle_at_top_left,_rgba(76,201,240,0.26),_transparent_36%),linear-gradient(160deg,_rgba(8,13,28,0.96),_rgba(12,25,47,0.94)_46%,_rgba(6,10,18,0.98))] bg-cover bg-center p-8 lg:flex lg:flex-col lg:justify-between"
              style={
                registrationBackgroundUrl
                  ? {
                      backgroundImage: `linear-gradient(180deg, rgba(5, 9, 18, 0.42) 0%, rgba(5, 10, 20, 0.7) 42%, rgba(5, 9, 16, 0.82) 100%), url(${registrationBackgroundUrl})`,
                    }
                  : undefined
              }
            >
              <div className="pointer-events-none absolute inset-0">
                <div className="absolute -left-14 top-10 h-36 w-36 rounded-full bg-cyan-300/16 blur-3xl" />
                <div className="absolute bottom-4 right-2 h-48 w-48 rounded-full bg-indigo-500/18 blur-3xl" />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),transparent_30%,rgba(6,10,18,0.14)_60%,rgba(6,10,18,0.26)_100%)]" />
              </div>

              <div className="relative z-10 max-w-sm">
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/6 px-3 py-1 text-[11px] uppercase tracking-[0.24em] text-cyan-100/80">
                  New Client Setup
                </div>
                <h2
                  className="text-[2.25rem] font-semibold leading-[1.02] text-white"
                  style={{ fontFamily: "'Inter', 'HarmonyOS Sans', -apple-system, BlinkMacSystemFont, sans-serif" }}
                >
                  Shape your workspace.
                </h2>
                <p className="mt-4 max-w-[16rem] text-sm leading-6 text-slate-200">
                  Add the essentials and we will take care of the rest.
                </p>
              </div>
            </div>

            <div className="flex items-center">
              <div className="w-full px-7 py-8 sm:px-9 sm:py-10 lg:px-12">
                <div className="mb-6 lg:hidden">
                  <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-400/15 bg-cyan-400/10 px-3 py-1 text-[11px] uppercase tracking-[0.24em] text-cyan-200">
                    New Client Setup
                  </div>
                  <h2 className="text-2xl font-semibold text-white">
                    Welcome to Brainiacs
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-slate-400">
                    We only need a few details to finish preparing your account.
                  </p>
                </div>

                <div className="mb-6 hidden lg:block">
                  <p className="text-xs uppercase tracking-[0.24em] text-cyan-200/80">
                    Registration
                  </p>
                  <h3 className="mt-3 text-[2rem] font-semibold leading-tight text-white">
                    Complete your profile
                  </h3>
                  <p className="mt-3 max-w-xl text-sm leading-6 text-slate-400">
                    A tighter, cleaner workspace starts with the right company context.
                    Fill in the essentials and we will finish setting up your Brainiacs account.
                  </p>
                </div>

                {error && (
                  <div className="mb-6 rounded-2xl bg-rose-500/10 px-4 py-3 text-sm text-rose-200 ring-1 ring-inset ring-rose-500/20">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid gap-x-6 gap-y-6 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-[13px] font-medium tracking-wide text-slate-300">
                        Full Name <span className="text-rose-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={userName}
                        onChange={(e) => handleFieldChange("fullName", e.target.value)}
                        required
                        placeholder="John Doe"
                        className={editableInputClass(fieldErrors.fullName)}
                      />
                      {fieldErrors.fullName && (
                        <p className="mt-1.5 text-xs text-rose-300">{fieldErrors.fullName}</p>
                      )}
                    </div>

                    <div>
                      <label className="mb-2 block text-[13px] font-medium tracking-wide text-slate-300">
                        Email Address <span className="text-rose-400">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          value={userEmail}
                          readOnly
                          className={readOnlyInputClass}
                        />
                        <span className="pointer-events-none absolute right-4 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-cyan-300/10 text-cyan-100">
                          <svg
                            className="h-3.5 w-3.5"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <rect x="5" y="11" width="14" height="10" rx="2" />
                            <path d="M8 11V8a4 4 0 1 1 8 0v3" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-x-6 gap-y-6 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-[13px] font-medium tracking-wide text-slate-300">
                        Company Name <span className="text-rose-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={clientName}
                        onChange={(e) => handleFieldChange("companyName", e.target.value)}
                        required
                        placeholder="Acme Inc."
                        className={editableInputClass(fieldErrors.companyName)}
                      />
                      {fieldErrors.companyName && (
                        <p className="mt-1.5 text-xs text-rose-300">{fieldErrors.companyName}</p>
                      )}
                    </div>

                    <div>
                      <GeoSelect
                        id="registration-language"
                        label="Preferred Language"
                        required
                        borderless
                        value={preferredLanguage}
                        onChange={setPreferredLanguage}
                        options={localeOptions}
                        placeholder="Select your language…"
                      />
                    </div>
                  </div>

                  <div className="grid gap-x-6 gap-y-6 md:grid-cols-2">
                    <div>
                      <GeoSelect
                        id="registration-country"
                        label="Country"
                        required
                        borderless
                        value={countryCode}
                        onChange={handleCountryChange}
                        options={countryOptions}
                        placeholder="Select your country…"
                        loading={countriesLoading}
                      />
                      {fieldErrors.country && (
                        <p className="mt-1.5 text-xs text-rose-300">{fieldErrors.country}</p>
                      )}
                    </div>

                    <div>
                      <GeoSelect
                        id="registration-state"
                        label="State / Province"
                        borderless
                        value={stateName}
                        onChange={(v) => {
                          userTouchedGeo.current = true;
                          setStateName(v);
                        }}
                        options={stateOptions}
                        placeholder="Select a state…"
                        loading={statesLoading}
                        disabled={!countryCode}
                      />
                    </div>
                  </div>

                  <div className="grid gap-x-6 gap-y-6 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-[13px] font-medium tracking-wide text-slate-300">
                        Job Title
                      </label>
                      <input
                        type="text"
                        value={jobTitle}
                        onChange={(e) => handleFieldChange("jobTitle", e.target.value)}
                        placeholder="e.g., Marketing Director"
                        className={editableInputClass(fieldErrors.jobTitle)}
                      />
                      {fieldErrors.jobTitle && (
                        <p className="mt-1.5 text-xs text-rose-300">{fieldErrors.jobTitle}</p>
                      )}
                    </div>

                    <div>
                      <label className="mb-2 block text-[13px] font-medium tracking-wide text-slate-300">
                        Company Website <span className="text-rose-400">*</span>
                      </label>
                      <input
                        type="url"
                        value={clientWebsite}
                        onChange={(e) => handleFieldChange("companyWebsite", e.target.value)}
                        onBlur={handleWebsiteBlur}
                        required
                        placeholder="https://acme.com"
                        className={editableInputClass(fieldErrors.companyWebsite)}
                      />
                      {fieldErrors.companyWebsite && (
                        <p className="mt-1.5 text-xs text-rose-300">{fieldErrors.companyWebsite}</p>
                      )}
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={!userName || !clientName || !clientWebsite || !countryCode}
                      className="w-full rounded-2xl bg-[linear-gradient(135deg,#7dd3fc_0%,#5260FF_45%,#2532a8_100%)] px-6 py-3.5 text-sm font-semibold text-white shadow-[0_16px_40px_rgba(37,50,168,0.35)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_20px_50px_rgba(82,96,255,0.36)] disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Complete Registration
                    </button>
                    <p className="mt-4 text-center text-xs leading-5 text-slate-500">
                      Used to personalize your workspace and finish your account setup.
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    // Pure card container — the route (/register) supplies the full-screen
    // background + centering (AnimatedBackground wrapper). No own background
    // layer: that painted an opaque rectangle over the route background and,
    // combined with the route's own centering, pushed the card off-center.
    <div className="relative z-10 w-full max-w-[1120px]">
      <div className="overflow-hidden rounded-[32px] border border-white/10 bg-[linear-gradient(180deg,rgba(14,18,28,0.97),rgba(8,11,18,0.96))] shadow-[0_40px_120px_rgba(0,0,0,0.45)] backdrop-blur-2xl">
        {renderContent()}
      </div>
    </div>
  );
};
